import React, { useMemo, useState } from "react";

// Public, no-auth-required developer portal at /developers.
// Showcases the API + webhooks with copy-pasteable cURL / Node / Python
// snippets. Keeps the WinterHorse tactical palette: solid #111113 bg,
// muted #14b8a6 accent, glass panels with subtle borders, Inter body
// and Press Start 2P only for the marquee headline.

// -------- Snippet helpers --------

const CURL_LIST_LEAGUES = `curl -X GET "https://api.league-hub.gg/api/v1/leagues?game=valorant&limit=10" \\
  -H "Authorization: Bearer lh_YOUR_KEY_HERE"`;

const NODE_LIST_LEAGUES = `// Node.js (built-in fetch — Node 18+)
const res = await fetch(
  "https://api.league-hub.gg/api/v1/leagues?game=valorant&limit=10",
  { headers: { Authorization: "Bearer lh_YOUR_KEY_HERE" } }
);
const data = await res.json();
console.log(\`\${data.total} leagues, showing \${data.leagues.length}\`);`;

const PY_LIST_LEAGUES = `# Python 3 — pip install httpx
import httpx

r = httpx.get(
    "https://api.league-hub.gg/api/v1/leagues",
    params={"game": "valorant", "limit": 10},
    headers={"Authorization": "Bearer lh_YOUR_KEY_HERE"},
)
data = r.json()
print(data["total"], "leagues")`;

const NODE_WEBHOOK_VERIFY = `// Express receiver — verify the HMAC signature.
import crypto from "node:crypto";
import express from "express";

const WEBHOOK_SECRET = process.env.LH_WEBHOOK_SECRET;
const app = express();

// Capture the RAW body — signing is over the bytes we received.
app.post(
  "/webhook",
  express.raw({ type: "application/json" }),
  (req, res) => {
    const sig = req.header("X-LeagueHub-Signature");        // sha256=<hex>
    const expected =
      "sha256=" +
      crypto.createHmac("sha256", WEBHOOK_SECRET)
            .update(req.body)
            .digest("hex");
    if (!sig || !crypto.timingSafeEqual(Buffer.from(sig), Buffer.from(expected))) {
      return res.sendStatus(401);
    }
    const event = JSON.parse(req.body.toString("utf8"));
    console.log("got", event.event, event.data);
    res.sendStatus(200);
  }
);`;

const PY_WEBHOOK_VERIFY = `# Flask receiver — verify the HMAC signature.
import hmac, hashlib, os
from flask import Flask, request, abort

WEBHOOK_SECRET = os.environ["LH_WEBHOOK_SECRET"].encode()
app = Flask(__name__)

@app.post("/webhook")
def webhook():
    sig = request.headers.get("X-LeagueHub-Signature", "")
    expected = "sha256=" + hmac.new(
        WEBHOOK_SECRET, request.get_data(), hashlib.sha256
    ).hexdigest()
    if not hmac.compare_digest(sig, expected):
        abort(401)
    event = request.get_json()
    print("got", event["event"], event["data"])
    return "", 200`;

// -------- Endpoint catalog (kept in sync with backend manually) --------

const ENDPOINTS = [
  {
    method: "GET",
    path:   "/api/v1/leagues",
    scope:  "leagues:read",
    summary: "Paginated league directory. Filter by game and status.",
  },
  {
    method: "GET",
    path:   "/api/v1/leagues/{league_id}/standings",
    scope:  "leagues:read",
    summary: "Aggregated league standings (wins, losses, points, goal diff).",
  },
  {
    method: "GET",
    path:   "/api/v1/teams/{team_id}/matches",
    scope:  "teams:read",
    summary: "Recent match history for a team.",
  },
  {
    method: "GET",
    path:   "/api/v1/players/{user_id}/history",
    scope:  "players:read",
    summary: "Recent match history for a player.",
  },
  {
    method: "GET",
    path:   "/api/v1/leaderboards/{game}",
    scope:  "leagues:read",
    summary: "Per-game leaderboard. Window: all-time, current 7d, last 7d.",
  },
];

const WEBHOOK_EVENTS = [
  "league.match.created",
  "league.match.result",
  "league.match.scheduled",
  "league.match.rescheduled",
  "league.team.joined",
  "league.team.left",
  "league.status.changed",
  "league.announcement",
  "user.badge.awarded",
  "league.boost.applied",
];

// -------- Small UI primitives --------

const Card = ({ children, className = "", ...rest }) => (
  <div
    className={`glass rounded-2xl p-6 ${className}`}
    {...rest}
  >
    {children}
  </div>
);

const SectionTitle = ({ id, children, kicker }) => (
  <div id={id} className="mb-4">
    {kicker && (
      <p className="text-[10px] tracking-[3px] text-[#14b8a6] mb-1.5 font-semibold">
        {kicker}
      </p>
    )}
    <h2 className="text-2xl md:text-3xl font-semibold text-white tracking-tight">
      {children}
    </h2>
  </div>
);

function CodeBlock({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-[rgba(20,184,166,0.20)] bg-[#0c1320] overflow-hidden"
    >
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-[rgba(20,184,166,0.15)] bg-[rgba(20,184,166,0.04)]">
        <span className="text-[10px] tracking-[2px] text-[#a3a3ac] uppercase">
          {label}
        </span>
        <button
          type="button"
          onClick={copy}
          data-testid={`${testid}-copy`}
          className="text-[10px] tracking-widest text-white/80 hover:text-[#14b8a6] transition-colors"
        >
          {copied ? "COPIED" : "COPY"}
        </button>
      </div>
      <pre className="p-4 text-[12.5px] leading-relaxed text-white/85 overflow-x-auto whitespace-pre">
        <code>{value}</code>
      </pre>
    </div>
  );
}

function Tabs({ tabs, activeId, setActiveId, testid }) {
  return (
    <div className="flex gap-1 mb-3" data-testid={`${testid}-tabs`}>
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => setActiveId(t.id)}
          data-testid={`${testid}-tab-${t.id}`}
          className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold tracking-widest uppercase transition-colors ${
            t.id === activeId
              ? "bg-[rgba(20,184,166,0.18)] text-white border border-[rgba(20,184,166,0.55)]"
              : "text-white/65 hover:text-white border border-transparent"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// -------- Page --------

export default function DeveloperPortalPage() {
  const [exampleTab, setExampleTab] = useState("curl");
  const [webhookTab, setWebhookTab] = useState("node");

  const exampleSnippet = useMemo(() => {
    if (exampleTab === "node")   return NODE_LIST_LEAGUES;
    if (exampleTab === "python") return PY_LIST_LEAGUES;
    return CURL_LIST_LEAGUES;
  }, [exampleTab]);

  const webhookSnippet = useMemo(() => {
    return webhookTab === "python" ? PY_WEBHOOK_VERIFY : NODE_WEBHOOK_VERIFY;
  }, [webhookTab]);

  return (
    <main
      data-testid="dev-portal-page"
      className="max-w-screen-lg mx-auto px-5 lg:px-8 pt-10 pb-24 page-z"
    >
      {/* Hero */}
      <header className="mb-12">
        <p className="text-[10px] tracking-[4px] text-[#14b8a6] mb-2 font-semibold">
          DEVELOPER PORTAL
        </p>
        <h1
          data-testid="dev-portal-title"
          className="font-display text-3xl md:text-5xl text-white tracking-[2px]"
          style={{ textShadow: "none" }}
        >
          BUILD WITH LEAGUE HUB
        </h1>
        <p className="mt-4 max-w-2xl text-[15px] text-[#a3a3ac] leading-relaxed">
          A clean REST API + signed webhooks for Discord bots, OBS overlays,
          Zapier flows, and custom dashboards. Mint a key, hit an endpoint,
          ship.
        </p>
        <div className="mt-6 flex flex-wrap gap-3" data-testid="dev-portal-cta-row">
          <a
            href="/api/v1/docs"
            target="_blank"
            rel="noreferrer"
            data-testid="dev-portal-swagger-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg btn-neon text-[12px] tracking-widest"
          >
            INTERACTIVE DOCS
            <span aria-hidden>→</span>
          </a>
          <a
            href="#quickstart"
            data-testid="dev-portal-quickstart-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg btn-ghost text-[12px] tracking-widest"
          >
            QUICK START
          </a>
          <a
            href="/api/openapi.json"
            target="_blank"
            rel="noreferrer"
            data-testid="dev-portal-openapi-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg btn-ghost text-[12px] tracking-widest"
          >
            OPENAPI JSON
          </a>
        </div>
      </header>

      {/* Quick start */}
      <section className="mb-12">
        <SectionTitle id="quickstart" kicker="01 · QUICK START">
          Three steps to your first request
        </SectionTitle>
        <div className="grid md:grid-cols-3 gap-4">
          <Card data-testid="qs-step-1">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] mb-2 font-semibold">
              STEP 1
            </p>
            <p className="text-white text-[15px] font-semibold mb-1">
              Mint an API key
            </p>
            <p className="text-[13px] text-[#a3a3ac] leading-relaxed">
              Sign in, open <span className="text-white/90">Settings → API
              Keys</span>, choose scopes, copy the key once.
            </p>
          </Card>
          <Card data-testid="qs-step-2">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] mb-2 font-semibold">
              STEP 2
            </p>
            <p className="text-white text-[15px] font-semibold mb-1">
              Send the Bearer header
            </p>
            <p className="text-[13px] text-[#a3a3ac] leading-relaxed">
              Every public endpoint expects{" "}
              <code className="text-[#14b8a6] bg-[rgba(20,184,166,0.10)] px-1.5 rounded">
                Authorization: Bearer lh_…
              </code>.
            </p>
          </Card>
          <Card data-testid="qs-step-3">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] mb-2 font-semibold">
              STEP 3
            </p>
            <p className="text-white text-[15px] font-semibold mb-1">
              Hit the API
            </p>
            <p className="text-[13px] text-[#a3a3ac] leading-relaxed">
              Try the request below in cURL, Node or Python — same shape on
              the wire.
            </p>
          </Card>
        </div>
        <div className="mt-5">
          <Tabs
            testid="dev-portal-example"
            activeId={exampleTab}
            setActiveId={setExampleTab}
            tabs={[
              { id: "curl",   label: "cURL" },
              { id: "node",   label: "Node.js" },
              { id: "python", label: "Python" },
            ]}
          />
          <CodeBlock
            testid="dev-portal-example-code"
            label={`${exampleTab.toUpperCase()} · GET /api/v1/leagues`}
            value={exampleSnippet}
          />
        </div>
      </section>

      {/* Endpoint catalog */}
      <section className="mb-12">
        <SectionTitle id="endpoints" kicker="02 · ENDPOINTS">
          Public read API
        </SectionTitle>
        <div className="grid gap-2" data-testid="dev-portal-endpoints">
          {ENDPOINTS.map((e) => (
            <div
              key={e.path}
              data-testid={`endpoint-${e.path}`}
              className="glass rounded-xl px-4 py-3 flex items-center gap-3"
            >
              <span className="font-mono text-[10px] tracking-widest px-2 py-1 rounded-md bg-[rgba(20,184,166,0.14)] text-[#14b8a6] border border-[rgba(20,184,166,0.40)]">
                {e.method}
              </span>
              <code className="font-mono text-[12.5px] text-white/90 flex-shrink-0">
                {e.path}
              </code>
              <span className="hidden md:block text-[12px] text-[#a3a3ac] flex-1 truncate">
                {e.summary}
              </span>
              <span className="font-mono text-[10px] tracking-widest text-white/55 hidden lg:inline">
                {e.scope}
              </span>
            </div>
          ))}
        </div>
        <p className="text-[12px] text-[#a3a3ac] mt-3">
          Full request/response schemas live in the{" "}
          <a
            href="/api/v1/docs"
            target="_blank"
            rel="noreferrer"
            className="text-[#14b8a6] hover:underline"
          >
            interactive Swagger UI
          </a>
          .
        </p>
      </section>

      {/* Webhooks */}
      <section className="mb-12">
        <SectionTitle id="webhooks" kicker="03 · WEBHOOKS">
          React to league events in real time
        </SectionTitle>
        <p className="text-[14px] text-[#a3a3ac] leading-relaxed mb-5 max-w-3xl">
          Subscribe a URL to one or more events. Every delivery carries an{" "}
          <code className="text-[#14b8a6] bg-[rgba(20,184,166,0.10)] px-1.5 rounded">
            X-LeagueHub-Signature: sha256=&lt;hex&gt;
          </code>{" "}
          header — verify by computing the HMAC-SHA256 of the raw request
          body with your webhook secret. Failed deliveries retry with
          exponential backoff (0s → 30s → 2m → 10m, max 4 attempts) and are
          persisted to a delivery log you can inspect any time.
        </p>

        <Card className="mb-5" data-testid="webhook-events-card">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] mb-3 font-semibold">
            EVENT REGISTRY
          </p>
          <div className="flex flex-wrap gap-2">
            {WEBHOOK_EVENTS.map((e) => (
              <span
                key={e}
                data-testid={`event-${e}`}
                className="font-mono text-[11px] px-2 py-1 rounded-md bg-[rgba(20,184,166,0.08)] text-white/90 border border-[rgba(20,184,166,0.22)]"
              >
                {e}
              </span>
            ))}
          </div>
        </Card>

        <Tabs
          testid="dev-portal-webhook"
          activeId={webhookTab}
          setActiveId={setWebhookTab}
          tabs={[
            { id: "node",   label: "Node.js (Express)" },
            { id: "python", label: "Python (Flask)" },
          ]}
        />
        <CodeBlock
          testid="dev-portal-webhook-code"
          label={`${webhookTab.toUpperCase()} · SIGNATURE VERIFICATION`}
          value={webhookSnippet}
        />
      </section>

      {/* Auth & rate limits */}
      <section className="mb-12">
        <SectionTitle id="auth" kicker="04 · AUTH & LIMITS">
          The fine print
        </SectionTitle>
        <div className="grid md:grid-cols-2 gap-4">
          <Card data-testid="auth-card-bearer">
            <p className="text-white font-semibold mb-2">Bearer keys</p>
            <p className="text-[13px] text-[#a3a3ac] leading-relaxed">
              Keys are <code className="text-[#14b8a6]">lh_</code>-prefixed,
              hashed at rest, and tied to scopes. Revoking a key takes effect
              instantly. Show your key once — we store only the SHA-256
              digest, so we can&apos;t show it back to you later.
            </p>
          </Card>
          <Card data-testid="auth-card-ratelimit">
            <p className="text-white font-semibold mb-2">Rate limits</p>
            <p className="text-[13px] text-[#a3a3ac] leading-relaxed">
              Per-key sliding window: <b>120 requests / minute</b>. Bursting
              past it returns <code className="text-[#14b8a6]">429</code>{" "}
              with a <code className="text-[#14b8a6]">Retry-After</code>{" "}
              header. Need a higher limit? Drop us a line.
            </p>
          </Card>
          <Card data-testid="auth-card-scopes">
            <p className="text-white font-semibold mb-2">Scopes</p>
            <p className="text-[13px] text-[#a3a3ac] leading-relaxed mb-2">
              Pick the smallest set you need:
            </p>
            <ul className="text-[12.5px] text-white/80 space-y-1 font-mono">
              <li>leagues:read · matches:read · teams:read</li>
              <li>players:read · webhooks:manage</li>
              <li className="text-[#14b8a6]">* (full access)</li>
            </ul>
          </Card>
          <Card data-testid="auth-card-webhooks">
            <p className="text-white font-semibold mb-2">Webhook delivery</p>
            <p className="text-[13px] text-[#a3a3ac] leading-relaxed">
              Receivers must respond <b>200-2xx</b> within{" "}
              <b>10 seconds</b>. Anything else (timeout, 4xx, 5xx) flips the
              delivery to <code className="text-[#14b8a6]">retrying</code>{" "}
              and schedules the next attempt. After 4 failed attempts the
              row is marked <code className="text-[#14b8a6]">failed</code>.
            </p>
          </Card>
        </div>
      </section>

      {/* Foot */}
      <section className="text-center pt-6 border-t border-[rgba(20,184,166,0.15)]">
        <p className="text-[12px] text-[#a3a3ac]">
          Need help?{" "}
          <a
            href="mailto:developers@league-hub.gg"
            className="text-[#14b8a6] hover:underline"
            data-testid="dev-portal-contact-link"
          >
            developers@league-hub.gg
          </a>
        </p>
      </section>
    </main>
  );
}
